/**
 * Organization Context
 *
 * Provides organization data and settings for multi-tenant architecture.
 * Handles organization detection from subdomain, branding, and settings.
 *
 * @module context/OrganizationContext
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api } from '../services/authService';

/**
 * Default organization settings
 */
const DEFAULT_ORGANIZATION = {
  id: null,
  name: 'WASSER',
  slug: 'default',
  subdomain: 'default',

  // Branding
  logoUrl: null,
  primaryColor: '#2C3E50',
  secondaryColor: '#3498DB',
  fontFamily: 'Inter, sans-serif',

  // Plan limits
  plan: 'free',
  maxUsers: 5,
  maxProducts: 100,
  maxOrders: 500,

  // Settings
  settings: {},

  // Status
  isLoaded: false,
  isError: false,
};

/**
 * Organization Context
 */
const OrganizationContext = createContext({
  organization: DEFAULT_ORGANIZATION,
  isLoading: true,
  error: null,
  refreshOrganization: () => {},
  updateOrganization: () => {},
  getOrganizationHeader: () => ({}),
});

/**
 * Extract organization subdomain from current hostname
 * @returns {string|null}
 */
function extractSubdomain() {
  const hostname = window.location.hostname;

  // Development: check for org query param
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    const orgParam = params.get('org');
    if (orgParam) return orgParam;

    // Check localStorage for dev org
    const devOrg = localStorage.getItem('dev_organization_subdomain');
    if (devOrg) return devOrg;

    return null; // Default org in development
  }

  // Production: extract from subdomain
  // Expected format: subdomain.wasser.kg
  const parts = hostname.split('.');

  // Handle direct domain access (wasser.kg)
  if (parts.length <= 2) {
    return null;
  }

  // Handle www.wasser.kg
  if (parts[0] === 'www') {
    return null;
  }

  // Return subdomain
  return parts[0];
}

/**
 * Organization Provider Component
 */
export function OrganizationProvider({ children }) {
  const [organization, setOrganization] = useState(DEFAULT_ORGANIZATION);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch organization data from API
   */
  const fetchOrganization = useCallback(async (subdomain) => {
    if (!subdomain) {
      // Use default organization
      setOrganization({
        ...DEFAULT_ORGANIZATION,
        isLoaded: true,
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Use public endpoint for subdomain lookup (no auth required)
      const response = await api.get(
        `/api/organizations/by-subdomain/${subdomain}`
      );

      const data = response.data;

      setOrganization({
        id: data.id,
        name: data.name,
        slug: data.slug,
        subdomain: data.subdomain,
        description: data.description,

        // Branding
        logoUrl: data.logo_url || data.logoUrl,
        primaryColor: data.primary_color || data.primaryColor || '#2C3E50',
        secondaryColor:
          data.secondary_color || data.secondaryColor || '#3498DB',
        fontFamily: data.font_family || data.fontFamily || 'Inter, sans-serif',

        // Domain
        customDomain: data.custom_domain || data.customDomain,
        customDomainVerified:
          data.custom_domain_verified || data.customDomainVerified,

        // Plan
        plan: data.plan || 'free',
        subscriptionStatus:
          data.subscription_status || data.subscriptionStatus || 'active',
        maxUsers: data.max_users || data.maxUsers || 5,
        maxProducts: data.max_products || data.maxProducts || 100,
        maxOrders: data.max_orders || data.maxOrders || 500,

        // Stats
        totalUsers: data.total_users || data.totalUsers || 0,
        totalProducts: data.total_products || data.totalProducts || 0,
        totalOrders: data.total_orders || data.totalOrders || 0,

        // Settings
        settings: data.settings || {},

        // Status
        isActive: data.is_active ?? data.isActive ?? true,
        isLoaded: true,
        isError: false,
      });
    } catch (err) {
      console.error('Failed to fetch organization:', err);
      const message =
        err.response?.status === 404
          ? 'Организация не найдена'
          : err.response?.data?.error ||
            err.message ||
            'Ошибка загрузки организации';
      setError(message);
      setOrganization({
        ...DEFAULT_ORGANIZATION,
        isLoaded: true,
        isError: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh organization data
   */
  const refreshOrganization = useCallback(() => {
    const subdomain = extractSubdomain();
    return fetchOrganization(subdomain);
  }, [fetchOrganization]);

  /**
   * Update organization settings locally (optimistic update)
   */
  const updateOrganization = useCallback((updates) => {
    setOrganization((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  /**
   * Get headers for API requests with organization context
   */
  const getOrganizationHeader = useCallback(() => {
    if (!organization.id) return {};
    return {
      'X-Organization-ID': organization.id,
    };
  }, [organization.id]);

  // Initial load
  useEffect(() => {
    const subdomain = extractSubdomain();
    fetchOrganization(subdomain);
  }, [fetchOrganization]);

  // Apply branding to document root
  useEffect(() => {
    if (organization.isLoaded && !organization.isError) {
      const root = document.documentElement;
      root.style.setProperty('--org-primary-color', organization.primaryColor);
      root.style.setProperty(
        '--org-secondary-color',
        organization.secondaryColor
      );
      root.style.setProperty('--org-font-family', organization.fontFamily);

      // Update page title if organization has name
      if (organization.name && organization.name !== 'WASSER') {
        document.title = `${organization.name} | WASSER`;
      }
    }
  }, [organization]);

  const contextValue = useMemo(
    () => ({
      organization,
      isLoading,
      error,
      refreshOrganization,
      updateOrganization,
      getOrganizationHeader,
    }),
    [
      organization,
      isLoading,
      error,
      refreshOrganization,
      updateOrganization,
      getOrganizationHeader,
    ]
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

/**
 * Hook to use organization context
 * @returns {Object} Organization context value
 */
export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}

/**
 * Hook to get organization ID for API calls
 * @returns {string|null}
 */
export function useOrganizationId() {
  const { organization } = useOrganization();
  return organization.id;
}

/**
 * Hook to check plan limits
 * @returns {Object} Limit checking functions
 */
export function useOrganizationLimits() {
  const { organization } = useOrganization();

  return useMemo(
    () => ({
      canAddUser: () => organization.totalUsers < organization.maxUsers,
      canAddProduct: () =>
        organization.totalProducts < organization.maxProducts,
      canAddOrder: () => organization.totalOrders < organization.maxOrders,

      usersRemaining: organization.maxUsers - organization.totalUsers,
      productsRemaining: organization.maxProducts - organization.totalProducts,
      ordersRemaining: organization.maxOrders - organization.totalOrders,

      plan: organization.plan,
      isFreePlan: organization.plan === 'free',
      isPaidPlan: ['starter', 'business', 'enterprise'].includes(
        organization.plan
      ),
    }),
    [organization]
  );
}

/**
 * Hook to get organization branding
 * @returns {Object} Branding values
 */
export function useOrganizationBranding() {
  const { organization } = useOrganization();

  return useMemo(
    () => ({
      name: organization.name,
      logoUrl: organization.logoUrl,
      primaryColor: organization.primaryColor,
      secondaryColor: organization.secondaryColor,
      fontFamily: organization.fontFamily,
    }),
    [
      organization.name,
      organization.logoUrl,
      organization.primaryColor,
      organization.secondaryColor,
      organization.fontFamily,
    ]
  );
}

export default OrganizationContext;
